import { CustomSelect } from '@theme/core-custom-select'
import { MediaQueryUtility } from '@theme/mixin-media-query-utility'
import {
  autoUpdate,
  computePosition,
  flip,
  offset,
} from '@theme/vendor-floating-ui'
import { html, map, ref, unsafeHTML } from '@theme/vendor-lit'

export class CountrySelect extends CustomSelect {
  static get properties() {
    return {
      label: { type: String },
      placeholder: { type: String, attribute: 'placeholder' },
      selectToggleIcon: { type: String, attribute: 'select-toggle-icon' },
      selectDropdownClass: { type: String, attribute: 'select-dropdown-class' },
      selectDropdownItemClass: {
        type: String,
        attribute: 'select-dropdown-item-class',
      },
      labelClass: { type: String, attribute: 'label-class' },
      selectToggleIconClass: {
        type: String,
        attribute: 'select-toggle-icon-class',
      },
      selectToggleClass: { type: String, attribute: 'select-toggle-class' },
      selectToggleIconSize: {
        type: String,
        attribute: 'select-toggle-icon-size',
      },
      selectName: { type: String, attribute: 'select-name' },
      currencySymbolSelected: {
        type: String,
        attribute: 'select-currency-symbol-selected',
      },
      currencyIsoCodeSelected: {
        type: String,
        attribute: 'select-currency-iso-code-selected',
      },
      selectDropdownSearchPlaceholder: {
        type: String,
        attribute: 'select-dropdown-search-placeholder',
      },
      selectDropdownTitle: { type: String, attribute: 'select-dropdown-title' },
      selectDropdownInfo: { type: String, attribute: 'select-dropdown-info' },
      currentCountryCode: {
        type: String,
        attribute: 'select-current-country-code',
      },
      currentCurrencySymbol: {
        type: String,
        attribute: 'select-current-currency-symbol',
      },
      currentCurrencyIsoCode: {
        type: String,
        attribute: 'select-current-currency-iso-code',
      },
      currentCurrencyLabel: {
        type: String,
        attribute: 'select-current-currency-label',
      },
      _searchQuery: { state: true },
    }
  }

  // The breakpoint at which the custom select will be rendered
  static CUSTOM_SELECT_BREAKPOINT = '(min-width: 0px)'

  constructor() {
    super()
    this.mediaQueryUtility = new MediaQueryUtility(
      this,
      CountrySelect.CUSTOM_SELECT_BREAKPOINT
    )

    this.label = ''
    this.placeholder = ''
    this.selectToggleIcon = ''
    this.selectDropdownClass = ''
    this.selectDropdownItemClass = ''
    this.labelClass = ''
    this.selectToggleIconClass = ''
    this.selectToggleClass = ''
    this.selectToggleIconSize = ''
    this.selectName = ''
    this.currencySymbolSelected = ''
    this.currencyIsoCodeSelected = ''
    this.selectDropdownSearchPlaceholder = ''
    this.selectDropdownTitle = ''
    this.selectDropdownInfo = ''
    this.currentCountryCode = ''
    this.currentCurrencySymbol = ''
    this.currentCurrencyIsoCode = ''
    this.currentCurrencyLabel = ''
    this._searchQuery = ''
    this.scrollbarWidth = 0
    this.calculateScrollbarWidth()
    window.addEventListener('resize', this.calculateScrollbarWidth.bind(this))
  }

  _collectionDataFromChildren() {
    const $options = Array.from(this.querySelectorAll('option'))

    if ($options.length === 0) return
    this._options = $options.map((option) => ({
      label: option.textContent || '',
      value: option.value || '',
      currencyCode: option.getAttribute('data-currency-code') || '',
      currencySymbol: option.getAttribute('data-currency-symbol') || '',
    }))
  }

  _positionListbox(toggleElement, listboxElement) {
    computePosition(toggleElement, listboxElement, {
      placement: 'bottom-start',
      strategy: 'absolute',
      middleware: [
        offset(5),
        flip({
          crossAxis: false,
          fallbackPlacements: ['top-start', 'bottom-start'],
          fallbackAxisSideDirection: 'start',
        }),
      ],
    }).then(({ x, y }) => {
      if (window.innerWidth <= 640) {
        y = 0
      }
      Object.assign(listboxElement.style, {
        left: `${x}px`,
        top: `${y}px`,
      })
    })
  }

  get labelHTML() {
    return html`
      <label
        for="${this.idForToggle}"
        class=${this.labelClass ? this.labelClass : CLASSES_MAPPING.LABEL}
      >
        ${this.label}
      </label>
    `
  }

  getDropdownItemHTML(option) {
    return html`
      <button
        type="button"
        class=${
          this.selectDropdownItemClass
            ? this.selectDropdownItemClass
            : CLASSES_MAPPING.DROPDOWN_ITEM
        }
        role="option"
        aria-selected="${option.selected}"
        value="${option.value}"
        ?selected=${option.selected}
        ?disabled=${option.disabled}
      >
        <span class="flex items-center">
          <span class="mr-2 h-3.5 w-3.5 overflow-hidden rounded-full">
            <img
              src="https://cdn.shopify.com/static/images/flags/${option.value.toLowerCase()}.svg"
              width="15px"
              class="h-full w-full object-cover"
              alt="${option.value}"
            />
          </span>
          ${option.label}
        </span>

        <span>
          ${option.currencyCode?.toLowerCase()} (${option.currencySymbol})
        </span>
      </button>
    `
  }

  _handleSearchInput(event) {
    const target = event.target
    this._searchQuery = target.value.toLowerCase()
  }

  get _filteredOptions() {
    if (this._searchQuery.length < 2) {
      return this._processedOptions
    }

    return this._processedOptions.filter((option) =>
      option.label.toLowerCase().includes(this._searchQuery)
    )
  }

  firstUpdated(_changedProperties) {
    super.firstUpdated(_changedProperties)
    this.setAttribute('name', 'country_code')
  }

  calculateScrollbarWidth() {
    this.scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth
  }

  toggleBodyScroll(expand) {
    const mainHeader = document.querySelector('.fixed-main-header')

    if (expand) {
      document.body.style.overflow = 'hidden'
      document.body.style.paddingRight = `${this.scrollbarWidth}px`
      if (mainHeader) {
        mainHeader.style.paddingRight = `${this.scrollbarWidth}px`
      }
    } else {
      document.body.style.overflow = ''
      document.body.style.paddingRight = ''
      if (mainHeader) {
        mainHeader.style.paddingRight = ''
      }
    }
  }

  _handleToggleClick() {
    this._expanded = !this._expanded
  }
  async _handleExpandedStateChange() {
    this._cleanupWatcher?.()
    this.$emit('toggle', { open: this._expanded })

    this.toggleBodyScroll(this._expanded)
    await this.updateComplete

    if (!this._expanded) {
      return
    }
    const toggleElement = this.toggleElement.value
    const listboxElement = this.listboxElement.value

    if (!toggleElement || !listboxElement) return
    // Leveraging the floating-ui library to position the listbox
    // and update the position when the window is resized
    // or the listbox is scrolled
    // Anchoring Logic allows the listbox to be positioned relative to the toggle element
    // depending on the available space in the viewport
    this._cleanupWatcher = autoUpdate(toggleElement, listboxElement, () =>
      this._positionListbox(toggleElement, listboxElement)
    )

    this._onExpanded()
  }

  get getCustomSelectHTML() {
    return html`
      <div
        class="group peer relative h-full w-full"
        ?error=${this.error}
        ?open=${this._expanded}
        @click=${this._handleClick}
      >
        <button
          ${ref(this.toggleElement)}
          type="button"
          role="combobox"
          @click=${this._handleToggleClick}
          aria-haspopup="listbox"
          class="${
            this.selectToggleClass
              ? this.selectToggleClass
              : CLASSES_MAPPING.TOGGLE
          } relative"
          id="${this.idForToggle}"
          value="${this.value}"
          aria-controls="${this.idForListbox}"
          aria-expanded="${this._expanded}"
          aria-disabled="${this.disabled}"
          ?disabled=${this.disabled}
          ${this.helpText ? `aria-describedby="${this.id}_text"` : ''}
        >
          <span class="block truncate">
            <span
              class="mr-0 hidden h-6 w-6 overflow-hidden rounded-full group-[.header-currency]:block lg:mr-2"
            >
              <img
                src="https://cdn.shopify.com/static/images/flags/${this.currentCountryCode.toLowerCase()}.svg"
                width="15px"
                class="h-full w-full object-cover"
                alt="${this.currentCountryCode}"
              />
            </span>

            <span class="hidden pr-2 group-[.footer-currency]:block">
              ${this.currentCurrencyLabel.toLowerCase()}
              (${this.currentCurrencyIsoCode.toLowerCase()}
              ${this.currentCurrencySymbol})
            </span>
          </span>
          <div
            class="${
              this.selectToggleIconClass
                ? this.selectToggleIconClass
                : CLASSES_MAPPING.TOGGLE_ICON
            } group-[.footer-currency]:block"
          >
            <svg-icon
              src="${this.selectToggleIcon || 'icon-select-arrows'}"
              class="text-inherit"
              style="font-size: ${
                this.selectToggleIconSize
                  ? `${this.selectToggleIconSize}px`
                  : '12px'
              }"
            ></svg-icon>
          </div>
        </button>
        <div
          class="!motion-reduce:group-[[open]]:transition-none !fixed left-0 right-0 top-0 translate-y-4 bg-p-darkest opacity-0 transition-[opacity,display,transform] duration-300 [transition-behavior:allow-discrete] group-[[open]]:h-[100vh] group-[[open]]:w-[100vh] group-[[open]]:translate-y-0 group-[[open]]:opacity-60 motion-reduce:transition-none sm:hidden"
        ></div>

        <div
          ${ref(this.listboxElement)}
          id="dropdown"
          role="listbox"
          id="${this.idForListbox}"
          ?inert=${!this._expanded}
          class=${
            this.selectDropdownClass
              ? this.selectDropdownClass
              : CLASSES_MAPPING.DROPDOWN
          }
          ${this.multiple ? 'aria-multiselectable="true"' : ''}
        >
          <div class="text-body text-p-darkest">
            <div
              class="flex h-16 items-center justify-between border-b border-p-dark px-5"
            >
              <div class="relative">${this.selectDropdownTitle}</div>
              <svg-icon
                src="${'icon-close'}"
                class="text-inherit cursor-pointer"
                @click=${this._handleToggleClick}
              ></svg-icon>
            </div>
            <div class="mt-5 border-b border-p-dark px-5 lowercase">
              <div class="mb-8">
                <p class="caption">
                  ${unsafeHTML(this.selectDropdownInfo.toLowerCase())}
                </p>
              </div>
              <input
                type="text"
                placeholder="${this.selectDropdownSearchPlaceholder}"
                class="caption mb-10 w-full border-b border-t-p-darkest py-2 text-[16px] text-p-darkest shadow-none !ring-0 placeholder:text-p-darkest"
                @input=${this._handleSearchInput}
              />
            </div>
          </div>
          ${map(this._filteredOptions, (option) => this.getDropdownItemHTML(option))}
        </div>
        ${this.labelHTML}
      </div>
      ${this.helpTextHTML}
    `
  }
}

customElements.define('country-select', CountrySelect)

const CLASSES_MAPPING = {
  TOGGLE:
    'text-left block w-full border-b border-p-lightest appearance-none text-p-lightest disabled:text-t-border px-sm-forms-padding lg:px-lg-forms-padding pt-sm-forms-padding lg:pt-1 h-sm-forms-inputheight lg:h-lg-forms-inputheight text-body disabled:border-t-border focus:outline-none focus:ring-0 peer group-[[error]]:ring-u-error group-[[error]]:border-[transparent] group-[[error]]:ring-2 focus-visible:ring-2 focus-visible:ring-u-focus',
  TOGGLE_ICON:
    'absolute inset-y-0 flex items-center size-3 pointer-events-none end-sm-forms-padding lg:end-sm-forms-padding text-p-light group-[[error]]:text-u-error h-sm-forms-inputheight lg:h-lg-forms-inputheigh',
  LABEL:
    'absolute caption text-t-foreground-secondary duration-300 transform -translate-y-2.5 scale-75 top-3.5 start-sm-forms-padding lg:start-sm-forms-padding z-10 origin-[0] peer-focus:text-t-foreground peer-disabled:text-t-border peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-2.5 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto',
  HELP_TEXT:
    'caption text-t-foreground-secondary start-sm-forms-padding lg:start-sm-forms-padding',
  DROPDOWN:
    'custom-select__dropdown min-w-max bg-c-form-bg max-h-[50vh] overflow-y-scroll overflow-x-clip scrollbar-thin scrollbar-track-[transparent] scrollbar-thumb-p-neutral absolute z-20 hidden top-full left-0 w-full border border-t-border bg-t-background drop-shadow-md rounded-forms-radius group-[[open]]:grid translate-y-4 opacity-0 transition-[opacity,display,transform] [transition-behavior:allow-discrete] motion-reduce:transition-none duration-300 group-[[open]]:translate-y-0 group-[[open]]:opacity-100 !motion-reduce:group-[[open]]:transition-none',
  DROPDOWN_ITEM:
    'px-forms-padding group ring-0 focus:ring-0 appearance-none outline-none py-xs bg-t-background text-body disabled:text-t-disabled text-p-darkest [&[selected]]:text-t-foreground [&[selected]]:bg-t-brand-secondary focus-visible:text-t-foreground hover:text-t-foreground [&:disabled]:hover:text-t-disabled text-left border-b border-t-foreground-secondary last:border-b-0 border-solid',
}
